import React from 'react';
import { Image, Pressable, StyleSheet, View } from 'react-native';
import { Text } from './Texte';
import { MaterialCommunityIcons } from './icons';
import { t } from '../i18n';
import { daysSince } from '../utils/relativeDate';
import { colors, radius, shadow, spacing, typography } from '../theme/tokens';
import type { NewsArticle } from '../types';

/**
 * Carte d'article du fil News.
 *
 * TOUT EST FACULTATIF SAUF L'IDENTIFIANT. Les articles viennent d'un CMS où les champs se
 * remplissent à la main : chaque bloc de cette carte sait disparaître seul, sans laisser de trou ni
 * de ligne vide. Une carte réduite à son titre reste une carte correcte.
 *
 * RATIO DE COUVERTURE CALCULÉ, jamais figé. Les images sortent aujourd'hui toutes en 1200×675, mais
 * coder 16/9 en dur ferait sauter la mise en page le jour où une image carrée est publiée — et ce
 * jour-là, personne ne penserait à ce fichier. La hauteur est donc dérivée des dimensions annoncées,
 * ce qui réserve la place AVANT le chargement : pas de saut quand l'image arrive.
 */
const RATIO_PAR_DEFAUT = 16 / 9;
const LOGO = spacing.xl;
const COUVERTURE_RADIUS = radius.lg;

/** Seuils de bascule du relatif. Au-delà d'un an on cesse de compter, la précision n'a plus d'objet. */
const JOURS_SEMAINE = 7;
const JOURS_MOIS = 30;
const JOURS_AN = 365;

/**
 * Rend une date « YYYY-MM-DD » en relatif, sur le QUANTIÈME seul (cf. `utils/relativeDate.ts`).
 * Renvoie `null` si la date manque ou ne tient pas : la ligne de pied s'en passe alors.
 */
export function relativeLabel(date: unknown, now?: Date): string | null {
  const jours = daysSince(date, now);
  if (jours === null) return null;
  // Une date à venir (article programmé) n'a pas de relatif sensé : on n'affiche rien plutôt
  // qu'« il y a -2 j ».
  if (jours < 0) return null;
  if (jours === 0) return t('news.relative.today');
  if (jours === 1) return t('news.relative.yesterday');
  if (jours < JOURS_SEMAINE) return t('news.relative.days', { count: jours });
  if (jours < JOURS_MOIS) return t('news.relative.weeks', { count: Math.floor(jours / JOURS_SEMAINE) });
  if (jours < JOURS_AN) return t('news.relative.months', { count: Math.floor(jours / JOURS_MOIS) });
  return t('news.relative.years', { count: Math.floor(jours / JOURS_AN) });
}

export interface ArticleCardProps {
  article: NewsArticle;
  favorite: boolean;
  onToggleFavorite: (article: NewsArticle) => void;
  liked: boolean;
  likeCount: number;
  onToggleLike: (article: NewsArticle) => void;
  onOpen: (article: NewsArticle) => void;
  /**
   * Non connecté : ni cœur ni signet — proposer une action qui échouerait est pire que ne rien
   * proposer. L'ouverture de l'article, elle, reste offerte à tout le monde.
   */
  canInteract?: boolean;
  now?: Date;
  testID?: string;
}

/**
 * TROIS ZONES DE TAP, SANS IMBRICATION.
 *
 * Le corps (ouvrir) et le pied (liker, enregistrer) sont FRÈRES, pas emboîtés. Un cœur posé à
 * l'intérieur du pressable d'ouverture déclencherait les deux : le like partirait ET l'article
 * s'ouvrirait. `stopPropagation` corrigerait l'apparence du problème sans corriger la structure —
 * et retomberait au premier remaniement.
 */
export function ArticleCard({
  article,
  favorite,
  onToggleFavorite,
  liked,
  likeCount,
  onToggleLike,
  onOpen,
  canInteract = true,
  now,
  testID = `news-article-${article.id}`,
}: ArticleCardProps) {
  const { publisher, coverImage } = article;
  const relatif = relativeLabel(article.date, now);
  const duree =
    typeof article.readingMinutes === 'number' && article.readingMinutes > 0
      ? t('news.readingTime', { count: article.readingMinutes })
      : null;

  const ratio =
    coverImage?.width && coverImage?.height ? coverImage.width / coverImage.height : RATIO_PAR_DEFAUT;

  // LA CATÉGORIE A QUITTÉ LE PIED. Noyée entre la date et la durée, en petit et en gris, elle ne se
  // lisait pas : c'est pourtant elle qui dit d'un coup d'œil si l'on a affaire à un podcast ou à un
  // conseil. Elle passe en pastille, en haut à droite — à l'opposé de l'éditeur, sur la ligne qui
  // reste vide de ce côté.
  const pied = [relatif, duree].filter(Boolean).join(' · ');

  /**
   * UNE CARTE = UN ÉLÉMENT ACCESSIBLE. Sans cela VoiceOver s'arrête sur le logo, le nom, l'image, le
   * titre, l'extrait et chaque fragment du pied — sept arrêts pour un seul article. Le signet reste
   * en dehors de ce groupe : c'est une ACTION, elle mérite son propre arrêt et son propre état.
   */
  const annonce = [
    publisher?.name,
    publisher?.verified ? t('news.verified') : null,
    // La catégorie reste annoncée : elle a changé de PLACE, pas de statut. La retirer de l'annonce
    // en même temps que du pied l'aurait fait disparaître pour qui n'a que la voix.
    article.category,
    article.title,
    article.excerpt,
    coverImage?.alt,
    pied,
  ]
    .filter(Boolean)
    .join('. ');

  return (
    <View style={styles.card} testID={testID}>
      <Pressable
        testID={`${testID}-open`}
        accessibilityRole="button"
        accessible
        accessibilityLabel={annonce}
        onPress={() => onOpen(article)}
        style={styles.corps}
      >
        {/* La ligne existe dès qu'il y a un éditeur OU une catégorie : sans cette condition, un
            article sans éditeur perdrait aussi sa pastille, alors qu'elle n'en dépend pas. */}
        {publisher || article.category ? (
          <View style={styles.enTete} testID={`${testID}-header`}>
            <View style={styles.editeur} testID={`${testID}-publisher`}>
              {publisher?.logoUrl ? (
                <Image
                  source={{ uri: publisher.logoUrl }}
                  style={styles.logo}
                  testID={`${testID}-publisher-logo`}
                />
              ) : null}
              {publisher?.name ? <Text style={styles.editeurNom}>{publisher.name}</Text> : null}
              {publisher?.verified ? (
                <MaterialCommunityIcons
                  testID={`${testID}-verified`}
                  name="check-decagram"
                  size={typography.small.size}
                  color={colors.electric}
                />
              ) : null}
            </View>

            {/* PASTILLE DE CATÉGORIE, à l'opposé de l'éditeur. `numberOfLines={1}` : « Nouveauté
                Horméia » est le libellé le plus long du site, et une pastille sur deux lignes
                déséquilibrerait la ligne d'en-tête. Pas de plafond de police pour autant — la
                pastille grandit avec le texte, elle ne le rogne pas. */}
            {article.category ? (
              <View style={styles.categorie} testID={`${testID}-category`}>
                <Text style={styles.categorieTexte} numberOfLines={1}>
                  {article.category}
                </Text>
              </View>
            ) : null}
          </View>
        ) : null}

        {coverImage?.url ? (
          <Image
            testID={`${testID}-cover`}
            source={{ uri: coverImage.url }}
            style={[styles.couverture, { aspectRatio: ratio }]}
            // `alt` ET `accessibilityLabel` : le premier est la prop moderne de RN, le second reste
            // ce que lisent les lecteurs d'écran sur les versions en service.
            alt={coverImage.alt}
            accessibilityLabel={coverImage.alt}
          />
        ) : null}

        {article.title ? (
          <Text style={styles.titre} testID={`${testID}-title`}>
            {article.title}
          </Text>
        ) : null}

        {/* Deux lignes MAXIMUM : l'extrait situe l'article, il ne le remplace pas. C'est une coupe
            d'affichage, pas un plafond de police — la taille suit le réglage de l'appareil. */}
        {article.excerpt ? (
          <Text style={styles.extrait} numberOfLines={2} testID={`${testID}-excerpt`}>
            {article.excerpt}
          </Text>
        ) : null}
      </Pressable>

      <View style={styles.pied}>
        {pied ? (
          <Text style={styles.piedTexte} testID={`${testID}-meta`}>
            {pied}
          </Text>
        ) : (
          <View style={styles.piedVide} />
        )}

        {canInteract ? (
          <Pressable
            testID={`${testID}-like`}
            accessibilityRole="button"
            // L'ÉTAT ET LE NOMBRE sont annoncés : « aimé, 12 » dit à la fois ce qui est fait et ce
            // que ça pèse. Le seul libellé d'action laisserait le compteur muet pour VoiceOver,
            // alors qu'il est la moitié de l'information.
            accessibilityState={{ selected: liked }}
            accessibilityLabel={liked ? t('news.like.remove') : t('news.like.add')}
            accessibilityValue={{ text: t('news.like.count', { count: likeCount }) }}
            onPress={() => onToggleLike(article)}
            hitSlop={spacing.sm}
            style={styles.action}
          >
            <MaterialCommunityIcons
              testID={`${testID}-like-icon`}
              name={liked ? 'heart' : 'heart-outline'}
              size={typography.h3.size}
              color={liked ? colors.corail : colors.grey500}
            />
            <Text style={styles.compteur} testID={`${testID}-like-count`}>
              {likeCount}
            </Text>
          </Pressable>
        ) : null}

        {canInteract ? (
          <Pressable
            testID={`${testID}-favorite`}
            accessibilityRole="button"
            // L'ÉTAT EST ANNONCÉ DEUX FOIS, et c'est voulu : `selected` pour les lecteurs qui
            // l'exploitent, et le libellé qui dit l'action à venir pour ceux qui ne lisent que lui.
            accessibilityState={{ selected: favorite }}
            accessibilityLabel={favorite ? t('news.favorite.remove') : t('news.favorite.add')}
            accessibilityValue={{ text: favorite ? t('news.favorite.added') : t('news.favorite.notAdded') }}
            onPress={() => onToggleFavorite(article)}
            hitSlop={spacing.sm}
            style={styles.action}
          >
            <MaterialCommunityIcons
              testID={`${testID}-favorite-icon`}
              name={favorite ? 'bookmark' : 'bookmark-outline'}
              size={typography.h3.size}
              color={favorite ? colors.electric : colors.grey500}
            />
          </Pressable>
        ) : null}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    padding: spacing.lg,
    gap: spacing.md,
    borderRadius: radius.xl,
    backgroundColor: colors.white,
    ...shadow.card,
  },
  corps: { gap: spacing.md },
  // L'éditeur pousse à gauche, la pastille est plaquée à droite. `flex: 1` sur le groupe de gauche
  // pour qu'un nom long tronque plutôt que de chasser la pastille hors de la carte.
  enTete: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
  editeur: { flex: 1, flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
  // Carré ARRONDI et non cercle : les logos d'éditeurs sont dessinés dans un carré, un masque rond
  // leur couperait les angles.
  logo: { width: LOGO, height: LOGO, borderRadius: radius.sm, backgroundColor: colors.grey100 },
  editeurNom: {
    fontSize: typography.small.size,
    fontWeight: typography.bodyMd.weight,
    color: colors.grey700,
  },
  // Discrète : c'est un repère, pas un titre. Fond gris clair et texte gris foncé — neutre, donc
  // valable pour n'importe quelle rubrique, le champ étant du texte libre côté CMS.
  categorie: {
    paddingVertical: spacing.xs / 2,
    paddingHorizontal: spacing.sm,
    borderRadius: radius.pill,
    backgroundColor: colors.grey100,
    flexShrink: 0,
  },
  categorieTexte: {
    fontSize: typography.small.size,
    fontWeight: typography.bodyMd.weight,
    color: colors.grey700,
  },
  couverture: {
    width: '100%',
    borderRadius: COUVERTURE_RADIUS,
    backgroundColor: colors.grey100,
  },
  titre: {
    fontSize: typography.h3.size,
    fontWeight: typography.h3.weight,
    lineHeight: typography.h3.lineHeight,
    color: colors.night,
  },
  extrait: {
    fontSize: typography.body.size,
    lineHeight: typography.body.lineHeight,
    color: colors.grey500,
  },
  pied: { flexDirection: 'row', alignItems: 'center', gap: spacing.md },
  piedTexte: { flex: 1, fontSize: typography.small.size, color: colors.grey500 },
  piedVide: { flex: 1 },
  action: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs, padding: spacing.xs },
  compteur: {
    fontSize: typography.small.size,
    fontWeight: typography.bodyMd.weight,
    color: colors.grey500,
  },
});
